import type { MeResponse } from '@immo/api-contract';
import { apiClient } from './api-client';
import { getAccessToken, setAccessToken } from './access-token-store';

/**
 * Discriminated outcome of `completeSignIn`. `not-provisioned` is the
 * documented "user authed but has no organisation membership" branch
 * (backend code `identity.auth.user-not-provisioned`); callers redirect
 * to `/access-denied`. `failed` is everything else.
 */
export type CompleteSignInOutcome =
  | { readonly kind: 'success' }
  | { readonly kind: 'not-provisioned' }
  | { readonly kind: 'failed'; readonly status: number };

/**
 * Tries to mint an access token from the refresh cookie. Returns `true`
 * iff the call succeeded and the store now holds a token. Used by
 * `fetchMe` to avoid making an unauthenticated `/auth/me` call on cold
 * boot — without this, the first request always 401s before the fetcher
 * silently retries against `/auth/refresh`.
 */
async function tryRefreshFromCookie(): Promise<boolean> {
  const result = await apiClient.auth.refresh({ body: {} });

  if (result.ok) {
    setAccessToken(result.body.accessToken);

    return true;
  }

  setAccessToken(null);

  return false;
}

/**
 * Returns the current member if a session exists, or `null` for "no session".
 * Cold-boot path: if the in-memory store is empty (e.g. tab just opened),
 * try `/auth/refresh` first using the cookie and only call `/auth/me` once
 * the store has a token. With a token already present, calls `/auth/me`
 * directly — the api-client's 401-retry handles stale tokens. Failures
 * other than 401 propagate so the global error boundary can surface them.
 */
export async function fetchMe(): Promise<MeResponse | null> {
  if (getAccessToken() === null) {
    const refreshed = await tryRefreshFromCookie();

    if (!refreshed) {
      return null;
    }
  }

  const result = await apiClient.auth.me();

  if (result.ok) {
    return result.body;
  }

  if (result.status === 401) {
    return null;
  }

  throw new Error(`Unexpected /auth/me status ${result.status}`);
}

/**
 * Kicks the user into the AuthKit redirect for the chosen provider. The
 * backend builds the URL (signed `state`, correct redirect URI); the SPA
 * just navigates to it.
 *
 * `opts.loginHint` pre-fills the e-mail on the IdP screen and `opts.forceLogin`
 * forces a fresh sign-in (prompt=login) instead of reusing a live WorkOS
 * session — both used by the member-onboarding connect-your-login step (IMM-271).
 *
 * `opts.returnTo` is the same-origin app path to land on after the round-trip
 * (IMM-486); the backend carries it inside the WorkOS `state` and the callback
 * re-validates it before navigating. Omitted by the onboarding connect step, so
 * the callback falls back to `/`.
 */
export async function startSignIn(
  provider: 'authkit' | 'google' | 'microsoft',
  opts: { loginHint?: string; forceLogin?: boolean; returnTo?: string } = {},
): Promise<void> {
  const result = await apiClient.auth.signIn({
    query: {
      provider,
      ...(opts.loginHint !== undefined ? { loginHint: opts.loginHint } : {}),
      // Query params serialise as strings; the contract parses it via z.stringbool.
      ...(opts.forceLogin !== undefined ? { forceLogin: String(opts.forceLogin) } : {}),
      ...(opts.returnTo !== undefined ? { returnTo: opts.returnTo } : {}),
    },
  });

  if (!result.ok) {
    throw new Error(`Sign-in URL request failed: ${result.status}`);
  }

  window.location.assign(result.body.url);
}

/**
 * Completes the code exchange. Returns a discriminated outcome so
 * the callback page can route differently for the `not-provisioned` case
 * (→ `/access-denied`) vs. other failures (→ generic error path). On
 * success the access token lands in the in-memory store while the refresh
 * cookie lands on `api.immoos.ai`.
 *
 * `state` is echoed back verbatim: it is how the backend knows which flow
 * minted this round-trip (IMM-529.4 carries `p:'google'` inside it), and
 * without it the direct Google branch is unreachable.
 */
export async function completeSignIn(code: string, state: string | null): Promise<CompleteSignInOutcome> {
  const result = await apiClient.auth.callback({
    body: { code, ...(state !== null && state.length > 0 ? { state } : {}) },
  });

  if (result.ok) {
    setAccessToken(result.body.accessToken);

    return { kind: 'success' };
  }

  if (result.body.error.code === 'identity.auth.user-not-provisioned') {
    return { kind: 'not-provisioned' };
  }

  return { kind: 'failed', status: result.status };
}

/**
 * Discriminated outcome of `exchangeImpersonation`. Unlike sign-in there is no
 * `not-provisioned` branch — a bad, reused, or expired handoff code all surface
 * as the neutral `identity.impersonation.handoff-not-redeemable` (HTTP 422), so
 * every non-success is folded into `failed`.
 */
export type ExchangeImpersonationOutcome =
  { readonly kind: 'success' } | { readonly kind: 'failed'; readonly status: number };

/**
 * Burns an admin impersonation handoff code for the opaque `imp_` operating
 * credential and seats it in the in-memory access-token store — from that point
 * the api-client sends it as the bearer and every request runs as the
 * impersonated member. The handoff code is single-use with a ~60s TTL, so this
 * must run immediately on landing. The credential is never persisted (no
 * cookie), so the impersonation session lives only for this tab's lifetime.
 */
export async function exchangeImpersonation(code: string): Promise<ExchangeImpersonationOutcome> {
  const result = await apiClient.impersonation.exchange({ body: { code } });

  if (result.ok) {
    setAccessToken(result.body.token);

    return { kind: 'success' };
  }

  return { kind: 'failed', status: result.status };
}

// Impersonation operating credentials are the only bearer that isn't a member
// JWT; the backend mints them with an `imp_` prefix (see the identity auth
// plugin). Matching it here lets the client tell an impersonated tab apart
// without a dedicated `/auth/me` flag.
const IMPERSONATION_TOKEN_PREFIX = 'imp_';

/**
 * True when the in-memory access token is an admin impersonation credential
 * rather than a member JWT — i.e. this tab was entered via the impersonation
 * handoff exchange. The credential is never persisted, so a reload drops it and
 * this returns false, which is exactly what should retire the banner.
 */
export function isImpersonationSession(): boolean {
  return getAccessToken()?.startsWith(IMPERSONATION_TOKEN_PREFIX) ?? false;
}

/**
 * Clears the in-memory access token, asks the backend to clear the refresh
 * cookie, and returns the WorkOS logout URL for the caller to navigate to.
 */
export async function signOut(): Promise<string> {
  const result = await apiClient.auth.signOut({ body: {} });
  setAccessToken(null);

  if (!result.ok) {
    throw new Error(`Sign-out failed: ${result.status}`);
  }

  return result.body.url;
}
